import { collectUserRawPermissionKeys, hasPermission } from '@/utils/permission'
import { getMobileNavHiddenTabIds, isMobileNavConfigured } from '@/utils/mobileNavPreference'
import {
  MOBILE_DESKTOP_EMBED_BASE,
  MOBILE_STANDALONE_BASE,
  mobilePathSuffix,
  joinMobilePath
} from '@/utils/mobileWorkbenchBase'

/** @param {object | null | undefined} u */
function isAdminLike(u) {
  return !!(u && (u.is_admin || u.is_superuser))
}

/** 与后端 _MOBILE_WORKBENCH_BACKFILL['mobile.inventory'] 对齐：具备任一库存业务权即视为可作业 */
const LEGACY_MOBILE_INVENTORY_KEYS = [
  'inventory.records.view',
  'inventory.records.edit',
  'inventory.records.review',
  'inventory.operations.view',
  'inventory.operations.edit',
  'inventory.operations.review',
  'inventory.initial_stock.review',
  'inventory.material_batches.view',
  'inventory.material_batches.edit',
  'inventory.material_batches.review',
  'inventory.locations.view',
  'inventory.locations.edit',
  'inventory.locations.review',
  'inventory.material_identities.purge',
  'inventory.view',
  'inventory.edit'
]

const LEGACY_MOBILE_ORDERS_KEYS = [
  'orders.sales.view',
  'orders.sales.edit',
  'orders.sales.review',
  'orders.view',
  'orders.edit'
]

const LEGACY_MOBILE_DISPATCH_KEYS = [
  'orders.dispatch.view',
  'orders.dispatch.edit',
  'orders.dispatch.review',
  'operations.execute'
]

const LEGACY_MOBILE_TASKS_KEYS = ['tasks.view', 'tasks.manage']

function legacyMobileInventory(u) {
  return LEGACY_MOBILE_INVENTORY_KEYS.some((k) => hasPermission(u, k))
}

function legacyMobileOrders(u) {
  return LEGACY_MOBILE_ORDERS_KEYS.some((k) => hasPermission(u, k))
}

function legacyMobileDispatch(u) {
  return LEGACY_MOBILE_DISPATCH_KEYS.some((k) => hasPermission(u, k))
}

function legacyMobileTasks(u) {
  return LEGACY_MOBILE_TASKS_KEYS.some((k) => hasPermission(u, k))
}

/** 角色上是否直接勾选 mobile.*（不受管理员 * 展开影响，专用于底栏显示） */
function hasRawMobilePerm(u, mobileKey) {
  if (!u || !mobileKey) return false
  return collectUserRawPermissionKeys(u).has(mobileKey)
}

function hasRawAnyPerm(u, keys) {
  const raw = collectUserRawPermissionKeys(u)
  return (keys || []).some((k) => raw.has(k))
}

/**
 * 移动底栏显示：须角色上勾选 mobile.* + 业务权限（管理员同样遵循 mobile.*，避免底栏全开）。
 * 页面/接口访问见 canAccessMobile*（管理员仍可进入各页面）。
 */
export function shouldShowMobileNavInventory(u) {
  if (!u) return false
  return hasRawMobilePerm(u, 'mobile.inventory') && legacyMobileInventory(u)
}

export function canAccessMobileInventory(u) {
  if (!u) return false
  if (isAdminLike(u)) return true
  return hasPermission(u, 'mobile.inventory') && legacyMobileInventory(u)
}

/** 是否具备出入库审批业务权（与库存编辑/旧审核别名解耦） */
export function canReviewInventoryOperationApproval(u) {
  if (!u) return false
  if (isAdminLike(u)) return true
  return (
    hasPermission(u, 'inventory.operation_approval.review') ||
    hasPermission(u, 'inventory.operation_approval.initial_review')
  )
}

/** 移动端库存出入库审批：须 mobile.inventory_operation_approval + 专用审批权 */
export function canAccessMobileInventoryOperationApproval(u) {
  if (!u) return false
  if (isAdminLike(u)) return true
  return (
    hasPermission(u, 'mobile.inventory_operation_approval') && canReviewInventoryOperationApproval(u)
  )
}

export function canAccessMobileOrders(u) {
  if (!u) return false
  if (isAdminLike(u)) return true
  return hasPermission(u, 'mobile.orders') && legacyMobileOrders(u)
}

export function shouldShowMobileNavOrders(u) {
  if (!u) return false
  return hasRawMobilePerm(u, 'mobile.orders') && legacyMobileOrders(u)
}

/** 移动工作台 · 订单一览表（客户→材料→颜色） */
export function canAccessMobileOrderOverview(u) {
  if (!u) return false
  if (isAdminLike(u)) return true
  if (hasPermission(u, 'mobile.order_overview')) {
    return (
      legacyMobileOrders(u) || hasPermission(u, 'orders.material_matching.view')
    )
  }
  return hasPermission(u, 'mobile.orders') && legacyMobileOrders(u)
}

/** 移动工作台 · 欠料板块（材料→颜色→客户） */
export function canAccessMobileMaterialShortage(u) {
  if (!u) return false
  if (isAdminLike(u)) return true
  if (hasPermission(u, 'mobile.material_shortage')) return true
  // 历史：从「订单」列表入口进入，须同时具备 mobile.orders 与配料查看权
  return (
    hasPermission(u, 'mobile.orders') && hasPermission(u, 'orders.material_matching.view')
  )
}

/** 底栏是否显示独立的「欠料」tab（仅勾选 mobile.material_shortage 时显示，不与订单 tab 混用） */
export function hasMobileMaterialShortageTab(u) {
  if (!u) return false
  return hasRawMobilePerm(u, 'mobile.material_shortage')
}

export function canAccessMobileDispatch(u) {
  if (!u) return false
  if (isAdminLike(u)) return true
  if (hasPermission(u, 'mobile.dispatch') && legacyMobileDispatch(u)) return true
  // 任务中心「待核销」打码：mobile.tasks + 派工业务权即可打开详情
  if (hasPermission(u, 'mobile.tasks') && legacyMobileTasks(u) && legacyMobileDispatch(u)) return true
  return false
}

export function shouldShowMobileNavDispatch(u) {
  if (!u) return false
  return hasRawMobilePerm(u, 'mobile.dispatch') && legacyMobileDispatch(u)
}

/** 包装称重子页：任意登录用户（保存后按操作人锁定） */
export function canAccessMobileDispatchPackaging(u) {
  return !!u
}

export function canAccessMobileTasks(u) {
  if (!u) return false
  if (isAdminLike(u)) return true
  return hasPermission(u, 'mobile.tasks') && legacyMobileTasks(u)
}

export function shouldShowMobileNavTasks(u) {
  if (!u) return false
  return hasRawMobilePerm(u, 'mobile.tasks') && legacyMobileTasks(u)
}

/** 移动工作站 · 配料管理：须同时具备 mobile.orders 与配料查看权 */
export function canAccessMobileMaterialMatching(u) {
  if (!u) return false
  if (isAdminLike(u)) return true
  return hasPermission(u, 'mobile.orders') && hasPermission(u, 'orders.material_matching.view')
}

const PROCESSING_BUSINESS_KEYS = [
  'purchase.processing.field',
  'purchase.processing_follow.view',
  'orders.material_matching.view',
  'orders.material_matching.edit',
  'orders.material_matching.review'
]

function hasProcessingBusinessAccess(u) {
  return PROCESSING_BUSINESS_KEYS.some((k) => hasPermission(u, k))
}

function hasMobileProcessingTab(u) {
  return (
    hasPermission(u, 'mobile.processing_orders') ||
    hasPermission(u, 'mobile.processing_follow') ||
    (hasPermission(u, 'mobile.orders') && hasProcessingBusinessAccess(u))
  )
}

/** 移动工作台 · 加工单列表：mobile.processing_orders 或（历史）mobile.orders + 加工作业权 */
export function canAccessMobileProcessingOrders(u) {
  if (!u) return false
  if (isAdminLike(u)) return true
  if (hasPermission(u, 'mobile.processing_orders') && hasProcessingBusinessAccess(u)) return true
  return hasPermission(u, 'mobile.orders') && hasProcessingBusinessAccess(u)
}

export function shouldShowMobileNavProcessingOrders(u) {
  if (!u) return false
  if (!hasProcessingBusinessAccess(u)) return false
  if (hasRawMobilePerm(u, 'mobile.processing_orders')) return true
  return hasRawMobilePerm(u, 'mobile.orders')
}

/** 加工单详情 / 入库 / 核销：加工作业权 +（移动加工入口 或 任务入口，与配色/市场采购一致） */
export function canAccessMobileProcessingDetail(u) {
  if (!u) return false
  if (isAdminLike(u)) return true
  if (!hasProcessingBusinessAccess(u)) return false
  return hasMobileProcessingTab(u) || hasPermission(u, 'mobile.tasks')
}

/** @deprecated 使用 canAccessMobileProcessingOrders */
export function canAccessMobileProcessingWorkbench(u) {
  return canAccessMobileProcessingOrders(u)
}

/**
 * 移动工作台 · 加工跟进：mobile.processing_follow + 任一加工作业权
 */
export function canAccessMobileProcessingFollowUp(u) {
  if (!u) return false
  if (isAdminLike(u)) return true
  if (!hasPermission(u, 'mobile.processing_follow')) return false
  return hasProcessingBusinessAccess(u)
}

export function shouldShowMobileNavProcessingFollowUp(u) {
  if (!u) return false
  if (!hasRawMobilePerm(u, 'mobile.processing_follow')) return false
  return hasProcessingBusinessAccess(u)
}

export function canAccessMobileColorFormulation(u) {
  if (!u) return false
  if (isAdminLike(u)) return true
  if (!hasPermission(u, 'mobile.color_formulation') && !hasPermission(u, 'mobile.tasks')) return false
  return (
    hasPermission(u, 'purchase.color_formulation.field') ||
    hasPermission(u, 'purchase.color_formulation.view') ||
    hasPermission(u, 'purchase.color_formulation.audit') ||
    hasPermission(u, 'orders.material_matching.view') ||
    hasPermission(u, 'orders.material_matching.edit')
  )
}

export function canAccessMobileMarketProcurement(u) {
  if (!u) return false
  if (isAdminLike(u)) return true
  if (!hasPermission(u, 'mobile.market_procurement') && !hasPermission(u, 'mobile.tasks')) return false
  return (
    hasPermission(u, 'purchase.market_procurement.field') ||
    hasPermission(u, 'purchase.market_procurement.view') ||
    hasPermission(u, 'purchase.market_procurement.audit') ||
    hasPermission(u, 'orders.material_matching.view') ||
    hasPermission(u, 'orders.material_matching.edit')
  )
}

export function canAccessMobileCashReimbursement(u) {
  if (!u) return false
  if (isAdminLike(u)) return true
  // 与后端 _can_submit_claims / _can_view_claims 及路由 permissionAny 对齐：
  // 仅 mobile.cash_reimbursement 即可报账；财务审核/查看权也可进入
  return (
    hasPermission(u, 'mobile.cash_reimbursement') ||
    hasPermission(u, 'finance.cash_reimbursement.submit') ||
    hasPermission(u, 'finance.cash_reimbursement.view') ||
    hasPermission(u, 'finance.cash_reimbursement.review') ||
    hasPermission(u, 'finance.view')
  )
}

export function canAccessMobileTestReports(u) {
  if (!u) return false
  if (isAdminLike(u)) return true
  if (!hasPermission(u, 'mobile.test_reports')) return false
  return (
    hasPermission(u, 'quality.test_reports.view') ||
    hasPermission(u, 'quality.test_reports.edit') ||
    hasPermission(u, 'quality.test_reports.delete')
  )
}

export function shouldShowMobileNavTestReports(u) {
  if (!u) return false
  if (!hasRawMobilePerm(u, 'mobile.test_reports')) return false
  return (
    hasPermission(u, 'quality.test_reports.view') ||
    hasPermission(u, 'quality.test_reports.edit') ||
    hasPermission(u, 'quality.test_reports.delete')
  )
}

export function shouldShowMobileNavCashReimbursement(u) {
  if (!u) return false
  return (
    hasRawMobilePerm(u, 'mobile.cash_reimbursement') ||
    hasRawAnyPerm(u, [
      'finance.cash_reimbursement.submit',
      'finance.cash_reimbursement.view',
      'finance.cash_reimbursement.review',
      'finance.view'
    ])
  )
}

/** 移动财务简报（财务/报账审核人员） */
export function canAccessMobileFinanceBrief(u) {
  if (!u) return false
  if (isAdminLike(u)) return true
  return (
    hasPermission(u, 'finance.view') ||
    hasPermission(u, 'finance.documents.view') ||
    hasPermission(u, 'finance.cash_reimbursement.view') ||
    hasPermission(u, 'finance.cash_reimbursement.review')
  )
}

/** 移动端应收入口：财务/管理员进完整看板，业务员进「我的应收」 */
export function getMobileReceivablesNavPath(user, basePath = MOBILE_STANDALONE_BASE) {
  const b = basePath.replace(/\/$/, '') || MOBILE_STANDALONE_BASE
  if (
    isAdminLike(user) ||
    hasPermission(user, 'finance.view') ||
    hasPermission(user, 'billing.statements.view')
  ) {
    return joinMobilePath(b, 'finance-reconciliation')
  }
  return joinMobilePath(b, 'my-receivables')
}

/** 移动「我的客户应收」（业务员，仅本人负责客户） */
export function canAccessMobileReceivables(u) {
  if (!u) return false
  if (isAdminLike(u)) return true
  return (
    hasPermission(u, 'finance.receivables.view') ||
    hasPermission(u, 'finance.view') ||
    hasPermission(u, 'billing.statements.view')
  )
}

export function shouldShowMobileNavReceivables(u) {
  if (!u) return false
  if (!canAccessMobileReceivables(u)) return false
  if (hasPermission(u, 'finance.view') || hasPermission(u, 'billing.statements.view')) {
    return hasRawMobilePerm(u, 'mobile.receivables')
  }
  return hasRawMobilePerm(u, 'mobile.receivables') || hasRawAnyPerm(u, ['finance.receivables.view'])
}

export function canReviewMobileCashReimbursement(u) {
  if (!u) return false
  if (isAdminLike(u)) return true
  return hasPermission(u, 'finance.cash_reimbursement.review') || hasPermission(u, 'finance.edit')
}

/** 移动端「他人报账」浏览（含仅查看权限；审核/付款仍须 canReview） */
export function canViewOthersMobileCashReimbursement(u) {
  if (!u) return false
  if (isAdminLike(u)) return true
  return (
    canReviewMobileCashReimbursement(u) ||
    hasPermission(u, 'finance.cash_reimbursement.view') ||
    hasPermission(u, 'finance.view')
  )
}

/**
 * 移动端底栏顺序：库存 → 订单 → 派工 → 任务
 * @returns {{ id: string, path: string, label: string, icon: string, match: (user: object | null | undefined) => boolean }[]}
 */
/**
 * @param {string} [basePath=/m]
 */
export function getMobileNavDefinitions(basePath = MOBILE_STANDALONE_BASE) {
  const b = basePath.replace(/\/$/, '') || MOBILE_STANDALONE_BASE
  return [
    { id: 'inventory', path: joinMobilePath(b, 'inventory'), label: '库存', icon: '📦', match: shouldShowMobileNavInventory },
    { id: 'orders', path: joinMobilePath(b, 'orders'), label: '订单', icon: '📝', match: shouldShowMobileNavOrders },
    {
      id: 'material_shortage',
      path: joinMobilePath(b, 'orders/shortage'),
      label: '欠料',
      icon: '⚠️',
      match: hasMobileMaterialShortageTab
    },
    {
      id: 'processing',
      path: joinMobilePath(b, 'processing'),
      label: '加工',
      icon: '🏭',
      match: shouldShowMobileNavProcessingOrders
    },
    {
      id: 'processing_follow',
      path: joinMobilePath(b, 'processing-follow-up'),
      label: '加工跟进',
      icon: '📌',
      match: shouldShowMobileNavProcessingFollowUp
    },
    { id: 'dispatch', path: joinMobilePath(b, 'dispatch'), label: '派工', icon: '📋', match: shouldShowMobileNavDispatch },
    {
      id: 'cash_reimbursement',
      path: joinMobilePath(b, 'cash-reimbursement'),
      label: '报账',
      icon: '💵',
      match: shouldShowMobileNavCashReimbursement
    },
    {
      id: 'test_reports',
      path: joinMobilePath(b, 'test-reports'),
      label: '测试报告',
      icon: '📄',
      match: shouldShowMobileNavTestReports
    },
    {
      id: 'receivables',
      path: joinMobilePath(b, 'my-receivables'),
      label: '应收',
      icon: '💰',
      match: shouldShowMobileNavReceivables
    },
    { id: 'tasks', path: joinMobilePath(b, 'task-center'), label: '任务', icon: '🎯', match: shouldShowMobileNavTasks }
  ]
}

const MOBILE_NAV_ACCESS_BY_ID = {
  inventory: canAccessMobileInventory,
  orders: canAccessMobileOrders,
  material_shortage: canAccessMobileMaterialShortage,
  processing: canAccessMobileProcessingOrders,
  processing_follow: canAccessMobileProcessingFollowUp,
  dispatch: canAccessMobileDispatch,
  cash_reimbursement: canAccessMobileCashReimbursement,
  test_reports: canAccessMobileTestReports,
  receivables: canAccessMobileReceivables,
  tasks: canAccessMobileTasks
}

function canAccessMobileNavById(u, tabId) {
  const fn = MOBILE_NAV_ACCESS_BY_ID[tabId]
  return fn ? fn(u) : false
}

/** 按角色/业务权限可显示的底栏项（不含个人隐藏设置） */
export function getEligibleMobileNavItems(user, basePath = MOBILE_STANDALONE_BASE) {
  return getMobileNavDefinitions(basePath).filter((def) => def.match(user))
}

/** 可在「底栏显示设置」中勾选的项（含管理员可访问的全部板块） */
export function getConfigurableMobileNavItems(user, basePath = MOBILE_STANDALONE_BASE) {
  return getMobileNavDefinitions(basePath).filter((def) => canAccessMobileNavById(user, def.id))
}

/** 是否仍能使用移动工作台（有底栏项、或可访问当前业务页） */
export function canUseMobileWorkbench(user, basePath = MOBILE_STANDALONE_BASE) {
  if (!user) return false
  if (isAdminLike(user)) return true
  if (getEligibleMobileNavItems(user, basePath).length > 0) return true
  return (
    canAccessMobileTasks(user) ||
    canAccessMobileInventory(user) ||
    canAccessMobileOrders(user) ||
    canAccessMobileDispatch(user) ||
    canAccessMobileProcessingDetail(user) ||
    canAccessMobileCashReimbursement(user) ||
    canAccessMobileTestReports(user) ||
    canAccessMobileReceivables(user)
  )
}

function withResolvedMobileNavPath(def, user, basePath) {
  if (def.id === 'receivables') {
    return { ...def, path: getMobileReceivablesNavPath(user, basePath) }
  }
  return def
}

/** @param {object | null | undefined} user @param {string} [basePath] */
export function getVisibleMobileNavItems(user, basePath = MOBILE_STANDALONE_BASE) {
  const hidden = getMobileNavHiddenTabIds(user)
  const source = isMobileNavConfigured(user)
    ? getConfigurableMobileNavItems(user, basePath)
    : getEligibleMobileNavItems(user, basePath)
  return source
    .filter((def) => !hidden.has(def.id))
    .map((def) => withResolvedMobileNavPath(def, user, basePath))
}

/** 桌面顶栏是否显示「移动工作台」入口 */
export function canAccessDesktopMobileWorkbench(user) {
  return (
    getVisibleMobileNavItems(user, MOBILE_DESKTOP_EMBED_BASE).length > 0 ||
    canUseMobileWorkbench(user, MOBILE_DESKTOP_EMBED_BASE)
  )
}

/**
 * @param {object | null | undefined} user
 * @param {string} [basePath=/m]
 */
export function getFirstAllowedMobilePath(user, basePath = MOBILE_STANDALONE_BASE) {
  const items = getVisibleMobileNavItems(user, basePath)
  if (items.length) return items[0].path
  if (canAccessMobileTasks(user)) return joinMobilePath(basePath, 'task-center')
  const configurable = getConfigurableMobileNavItems(user, basePath)
  if (configurable.length) return configurable[0].path
  return joinMobilePath(basePath, 'home')
}

/**
 * @param {string} path /m/... 或 /desktop-mobile/...
 * @param {object | null | undefined} user
 */
export function canAccessMobilePath(path, user) {
  const suffix = mobilePathSuffix(path)
  if (suffix === '/home' || suffix.startsWith('/home/')) return true
  if (suffix.startsWith('/inventory-approval/')) return canAccessMobileInventoryOperationApproval(user)
  if (suffix.startsWith('/inventory-approvals')) return canAccessMobileInventoryOperationApproval(user)
  if (suffix.startsWith('/inventory-transaction/')) return canAccessMobileTasks(user)
  if (suffix.startsWith('/inventory') || suffix.startsWith('/warehouse')) return canAccessMobileInventory(user)
  if (suffix.startsWith('/matching')) {
    const sub = suffix.slice('/matching'.length)
    const seg = sub.replace(/^\//, '').split('/')[0].split('?')[0]
    if (seg && /^\d+$/.test(seg)) {
      return canAccessMobileMaterialMatching(user) || canAccessMobileProcessingFollowUp(user)
    }
    return canAccessMobileMaterialMatching(user)
  }
  if (suffix.startsWith('/processing-follow-up')) return canAccessMobileProcessingFollowUp(user)
  if (suffix.startsWith('/market-procurement')) return canAccessMobileMarketProcurement(user)
  if (suffix.startsWith('/cash-reimbursement')) return canAccessMobileCashReimbursement(user)
  if (suffix.startsWith('/test-reports')) return canAccessMobileTestReports(user)
  if (suffix.startsWith('/finance-brief')) return canAccessMobileFinanceBrief(user)
  if (suffix.startsWith('/my-receivables') || suffix.startsWith('/finance-reconciliation')) {
    return canAccessMobileReceivables(user)
  }
  if (suffix.startsWith('/color-formulation')) return canAccessMobileColorFormulation(user)
  if (suffix.startsWith('/processing')) {
    if (suffix === '/processing' || suffix === '/processing/') {
      return canAccessMobileProcessingOrders(user) || canAccessMobileProcessingFollowUp(user)
    }
    return canAccessMobileProcessingDetail(user)
  }
  if (suffix.startsWith('/orders/overview')) return canAccessMobileOrderOverview(user)
  if (suffix.startsWith('/orders/shortage')) return canAccessMobileMaterialShortage(user)
  if (suffix.startsWith('/orders')) return canAccessMobileOrders(user)
  if (/^\/dispatch\/\d+\/packaging(\/|$)/.test(suffix)) {
    return canAccessMobileDispatchPackaging(user)
  }
  if (suffix.startsWith('/dispatch')) return canAccessMobileDispatch(user)
  if (suffix.startsWith('/manual-task')) return canAccessMobileTasks(user)
  if (suffix.startsWith('/task-center')) return canAccessMobileTasks(user)
  return true
}
